<script setup lang="ts">
/**
 * SG · Appoint / Withdraw Director — E-Filing (self-contained).
 *   B32114 Appoint/Withdraw CEO · B32115 Appoint/Withdraw MD 도 같은 BM03B — positionHeld 만 LC_CEO / LC_MD.
 *
 * Document (Preset Studio) 에서 작성한 선임/해임 내용을 자동 시드하고, 운영자가 보완 후 제출.
 * bizfile(BM03B) save-draft 규격에 맞춰 payload 를 구성한다.
 *  · idType : NRIC-C / FIN / PP  (combobox)
 *  · addressType : S(Local, Singapore) / F(Foreign)  (radio) — python 은 우리가 보낸 addPositionHolder 를 그대로 사용
 *  · mobile : countryCode / areaCode / number
 *  · date  : 세션 dateFormat 표시(내부 저장은 ISO)
 *  · cessation reason : 목록 + Others 선택 시 직접 입력
 *
 * ⚠ 기존 appoint-director / withdraw-director(공용 Officer 폼, python 미연동) 소스에 의존/영향 없음.
 */
import { ref, reactive, computed, watch } from 'vue';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { apiClient } from '@/shared/api/client';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import { saveEFilingData, updateEFilingComplete } from '@/features/startTask/api/taskApi';
import { useDialog } from '@/shared/composables/useDialog';
import { formatDateOnly, normalizeDateInputToIso } from '@/shared/composables/dateUtils';
import SgPyLoginDialog from '@/features/startTask/preset_task/sg/components/change-financial-year-end/SgPyLoginDialog.vue';
import {
  loadCmBaseCodeOptions, getB04Options, resolveCmBaseCodeValue,
} from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import { baseCodeApi as _baseCodeApiForB04 } from '@/shared/api/baseCodeApi';

interface Props { taskData?: any; taskId?: string; entityCd?: string; readOnly?: boolean }
const props = withDefaults(defineProps<Props>(), { taskData: null, taskId: '', entityCd: '', readOnly: false });
const emit = defineEmits<{ back: []; submit: [payload: { draftId: string; result: any }]; error: [error: any] }>();

const { warning: showWarning, alert: showAlert } = useDialog();
const sessionStore = useSessionStore();

const ID_TYPE_OPTIONS = [
  { title: 'NRIC', value: 'NRIC-C' },
  { title: 'FIN', value: 'FIN' },
  { title: 'Passport', value: 'PP' },
];
const ADDRESS_TYPES = [
  { title: 'Local (Singapore)', value: 'S' },
  { title: 'Foreign', value: 'F' },
];
const CESSATION_REASONS = [
  { title: 'Resignation', value: 'Resignation' },
  { title: 'Cessation', value: 'Cessation' },
  { title: 'Deceased', value: 'Deceased' },
  { title: 'Removal', value: 'Removal' },
  { title: 'Others', value: 'OTH' },
];

const st = (v: unknown) => (v == null ? '' : String(v).trim());
const fmtDate = (iso: string) => (iso ? formatDateOnly(iso) : '');
// 달력(v-date-picker) ↔ ISO(YYYY-MM-DD) 변환
function isoToDate(iso: string): Date | null {
  const m = st(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}
function pickedToIso(d: any): string {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

// 국가 공통코드(B05) — 인코퍼레이션과 동일 소스. bigo3 = 국가 전화코드(+82 등).
// 즉시 로드(setup 시점) + Promise 보관 → enrich 가 옵션 준비를 기다렸다가 매칭한다.
const countryOptions = ref<Array<{ title: string; value: string; bigo3: string }>>([]);
const countryReady: Promise<void> = (async () => {
  try {
    const res: any = await baseCodeApi.getCodesByMainCode('CM', 'B05', undefined);
    const codes = res?.codes || res?.data?.codes || [];
    countryOptions.value = codes.map((c: any) => ({
      title: c.defaultCodeName || c.DEFAULT_CODE_NAME || c.codeName || c.CODE_NAME || '',
      value: c.totalCode || c.TOTAL_CODE || '',
      bigo3: c.bigo3 || c.BIGO3 || '',
    })).filter((o: any) => o.title && o.value);
  } catch (e) { console.warn('[AWO] B05 country load failed', e); }
})();
const countryBigo3 = (cd: string) => (countryOptions.value.find((o) => o.value === cd)?.bigo3 || '').replace(/^\+/, '');

// 국적 공통코드(B04). bizfile 은 "nationality" 필드에 ISO2 코드(BIGO1, 예: "KR") 를 원하는데
// 우리 CM.B04 는 TOTAL_CODE 가 내부코드("B04102" 등)이고 BIGO1 이 ISO2 이다.
// 그래서 별도 캐시를 만들어 TOTAL_CODE → BIGO1 매핑을 확보한다.
const nationalityReady: Promise<void> = loadCmBaseCodeOptions('B04').then(() => undefined).catch(() => undefined);
const b04Bigo1Map: Record<string, string> = {};   // TOTAL_CODE → BIGO1(ISO2)
const b04TitleToBigo1: Record<string, string> = {}; // title(lower) → BIGO1
const b04Bigo1ToTotal: Record<string, string> = {}; // BIGO1(ISO2) → TOTAL_CODE
/** Nationality 콤보박스 목록(B04) — value = TOTAL_CODE. 보낼 때 toNationalityCode 가 ISO2 로 바꾼다. */
const nationalityOptions = ref<Array<{ title: string; value: string }>>([]);
const b04Bigo1Ready: Promise<void> = (async () => {
  try {
    const res: any = await _baseCodeApiForB04.getCodesByMainCode('CM', 'B04', undefined);
    const codes = res?.codes || res?.data?.codes || [];
    const opts: Array<{ title: string; value: string }> = [];
    for (const c of codes) {
      const total = String(c.totalCode || c.TOTAL_CODE || '').trim();
      const bigo1 = String(c.bigo1 || c.BIGO1 || '').trim().toUpperCase();
      const title = String(c.defaultCodeName || c.DEFAULT_CODE_NAME || c.codeName || c.CODE_NAME || '').trim();
      if (total && bigo1) b04Bigo1Map[total] = bigo1;
      if (title && bigo1) b04TitleToBigo1[title.toLowerCase()] = bigo1;
      if (total && bigo1 && !b04Bigo1ToTotal[bigo1]) b04Bigo1ToTotal[bigo1] = total;
      if (total && title) opts.push({ title, value: total });
    }
    nationalityOptions.value = opts;
  } catch (e) { console.warn('[AWO] B04 BIGO1 load failed', e); }
})();
/** 국적 원문(B04 코드 / ISO2 / 표시명)을 콤보박스 값(B04 TOTAL_CODE)으로. 못 찾으면 원문 유지. */
function toNationalityTotal(raw: string): string {
  const s = st(raw);
  if (!s) return '';
  if (b04Bigo1Map[s]) return s;
  if (/^[A-Za-z]{2}$/.test(s)) return b04Bigo1ToTotal[s.toUpperCase()] || s;
  const byTitle = nationalityOptions.value.find((o) => o.title.toLowerCase() === s.toLowerCase());
  if (byTitle) return byTitle.value;
  return resolveCmBaseCodeValue(s, getB04Options()) || s;
}
/** 행의 국적을 콤보박스 값으로 맞춘다(시드·복원·컨택트 보충 뒤). */
async function normalizeNationalities() {
  await nationalityReady;
  await b04Bigo1Ready;
  for (const row of appointRows) row.nationality = toNationalityTotal(row.nationality);
}
/** 국적명("Korean, South" / "KR" / "대한민국" / "B04102" 등)을 bizfile ISO2(BIGO1)로 정규화. */
function toNationalityCode(raw: string): string {
  const s = st(raw);
  if (!s) return '';
  // 이미 ISO2 (알파벳 2자) 면 그대로 반환.
  if (/^[A-Za-z]{2}$/.test(s)) return s.toUpperCase();
  // B04 TOTAL_CODE(예: "B04102") → BIGO1.
  if (b04Bigo1Map[s]) return b04Bigo1Map[s];
  // 표시명(예: "Korean, South") → BIGO1.
  const byTitle = b04TitleToBigo1[s.toLowerCase()];
  if (byTitle) return byTitle;
  // 폴백 — cmBaseCodeOptions 로 해석해서 TOTAL_CODE 얻은 뒤 다시 BIGO1 매핑.
  const total = resolveCmBaseCodeValue(s, getB04Options());
  if (total && b04Bigo1Map[total]) return b04Bigo1Map[total];
  return s;
}

// ────────── Singapore 주소 자동 감지/파싱 ────────── //
// 문서/컨택트가 자유서식 문자열 한 줄로 주소를 준 경우 SG 로컬 주소면 필드 파싱, 아니면 foreign.
//   예: "42 CHAI CHEE STREET, #10-60, SINGAPORE 461042"
//     → blkhseNo:"42", streetName:"CHAI CHEE STREET", levelNo:"10", unitNo:"60", postalCode:"461042", type:"S"
function isSgAddressText(full: string): boolean {
  const s = st(full).toUpperCase();
  if (!s) return false;
  if (/\bSINGAPORE\b/.test(s)) return true;
  if (/\b\d{6}\b/.test(s)) return true;   // SG postal code 6자리
  return false;
}
function parseSgAddress(full: string): Partial<Addr> {
  const s = st(full);
  if (!s) return {};
  const out: Partial<Addr> = { type: 'S' };
  // postalCode: 마지막 6자리 숫자
  const pc = s.match(/(?:^|\D)(\d{6})(?!\d)/);
  if (pc) out.postalCode = pc[1];
  // level-unit: "#10-60" 또는 "#10 - 60"
  const lu = s.match(/#\s*(\d+)\s*-\s*(\w+)/);
  if (lu) { out.levelNo = lu[1]; out.unitNo = lu[2]; }
  // block/street: postal, #unit, "SINGAPORE" 을 다 제거한 뒤 첫 번째 콤마 왼쪽 = block+street.
  let head = s
    .replace(/,?\s*SINGAPORE\s*\d{6}\b.*$/i, '')
    .replace(/,?\s*#\s*\d+\s*-\s*\w+/, '')
    .trim();
  head = head.replace(/,\s*$/, '').trim();
  //   "42 CHAI CHEE STREET" → block="42", street="CHAI CHEE STREET"
  const bs = head.match(/^(\S+)\s+(.+)$/);
  if (bs) {
    out.blkhseNo = bs[1];
    out.streetName = bs[2].replace(/,$/, '').trim();
  } else if (head) {
    out.streetName = head;
  }
  out.foreignAddr1 = '';
  out.foreignAddr2 = '';
  return out;
}
// 컨택트의 COUNTRY_CD(코드/국가명/전화코드 등) 를 드롭다운 value(totalCode)로 해석.
function resolveCountry(raw: string): string {
  const t = st(raw); if (!t) return '';
  const opts = countryOptions.value;
  if (opts.some((o) => o.value === t)) return t;
  const low = t.toLowerCase();
  const m = opts.find((o) => [o.title, o.bigo3].map((x) => String(x || '').trim().toLowerCase()).includes(low));
  return m?.value || '';
}

// 자유서식 주소 → foreign line1/line2 로 분할(중앙 근처 쉼표 기준).
function splitAddr(full: string): [string, string] {
  const s = st(full);
  if (s.length <= 55) return [s, ''];
  const mid = Math.floor(s.length / 2);
  const idxs: number[] = []; let p = s.indexOf(',');
  while (p !== -1) { idxs.push(p); p = s.indexOf(',', p + 1); }
  let best = -1;
  for (const i of idxs) if (best === -1 || Math.abs(i - mid) < Math.abs(best - mid)) best = i;
  if (best === -1) return [s.slice(0, 55).trim(), s.slice(55).trim()];
  return [s.slice(0, best + 1).trim(), s.slice(best + 1).trim()];
}
// idType 자동추론: 문서에 있으면 그대로, 없으면 ID 번호 형식으로. (NRIC-C / FIN / PP)
function mapIdType(raw: string, idNo: string): string {
  const r = st(raw).toUpperCase();
  if (r === 'NRIC-C' || r === 'FIN' || r === 'PP') return r;
  if (r.includes('NRIC')) return 'NRIC-C';
  if (r.includes('FIN')) return 'FIN';
  if (r.includes('PASSPORT') || r === 'PP') return 'PP';
  const id = st(idNo).toUpperCase();
  if (/^[ST]\d{7}[A-Z]$/.test(id)) return 'NRIC-C';
  if (/^[FGM]\d{7}[A-Z]$/.test(id)) return 'FIN';
  return id ? 'PP' : '';
}

const fd = computed<any>(() => props.taskData?.formData || props.taskData || {});
/** 이 E-Filing 을 쓰는 태스크 코드 — B32111(선임+해임) 외에 B32101 Appoint Director(선임만, 다건)도 같은 BM03B 를 쓴다.
 *  B32101 패널은 TASK_DATA.formData.taskTypeCd 를 남긴다. */
const taskTypeCd = computed(() => {
  const v = fd.value || {};
  const cd = st(v.taskTypeCd).toUpperCase();
  if (cd) return cd;
  // taskTypeCd 를 안 남긴 예전 CEO/MD 태스크 — 패널 고유 키로 판별.
  if ('ceoName' in v || 'ceoAction' in v) return 'B32114';
  if ('mdName' in v || 'mdAction' in v) return 'B32115';
  return 'B32111';
});
const appointOnly = computed(() => taskTypeCd.value === 'B32101');
const pick = (...vals: any[]) => { for (const v of vals) if (v != null && String(v).trim() !== '') return v; return ''; };

/** 직책별 E-Filing — CEO/MD 는 Director 와 같은 BM03B 호출, positionHeld 만 다르다. (key = 패널 변수 접두어) */
const POSITION_TASKS: Record<string, { code: string; label: string; key: string }> = {
  B32114: { code: 'LC_CEO', label: 'CEO', key: 'ceo' },
  B32115: { code: 'LC_MD', label: 'Managing Director', key: 'md' },
};
const positionTask = computed(() => POSITION_TASKS[taskTypeCd.value] || null);
const positionHeld = computed(() => positionTask.value?.code || 'Director');
const positionLabel = computed(() => positionTask.value?.label || 'Director');
/** CEO/MD 결의 종류(appoint / withdraw / both) — 고른 쪽 섹션만 보인다. */
const positionAction = computed(() => {
  const v = fd.value || {};
  const k = positionTask.value?.key || '';
  const preset = v.presetForm && typeof v.presetForm === 'object' ? v.presetForm : {};
  return st(pick(preset.action, k ? v[`${k}Action`] : '')) || 'both';
});
const showAppoint = computed(() => !positionTask.value || positionAction.value !== 'withdraw');
const showWithdraw = computed(() => !appointOnly.value && (!positionTask.value || positionAction.value !== 'appoint'));
const pageTitle = computed(() => {
  if (appointOnly.value) return 'Appoint Director';
  if (!positionTask.value) return 'Appoint / Withdraw Director';
  const l = positionLabel.value;
  if (positionAction.value === 'appoint') return `Appoint ${l}`;
  if (positionAction.value === 'withdraw') return `Withdraw ${l}`;
  return `Appoint / Withdraw ${l}`;
});
const emptyWho = computed(() => (positionTask.value ? `the ${positionLabel.value} was` : 'directors were'));

const entityName = ref('');
const entityUEN = ref('');
const entityType = ref('LC');
const filingAgentUEN = ref('');

interface Addr { type: 'S' | 'F'; blkhseNo: string; streetName: string; buildingName: string; levelNo: string; unitNo: string; postalCode: string; foreignAddr1: string; foreignAddr2: string }
interface AppointRow {
  name: string; registrationIdNo: string; idType: string; nationality: string; entryDate: string; contactCd: string;
  // 폼에 노출은 안 하지만 payload 에 실어야 하는 필드 (컨택트/문서에서 채움).
  birthDate: string; profileId: string;
  resi: Addr; contactSame: boolean; contact: Addr;
  email: string; countryCd: string; noAreaCode: boolean; mobileNoAreaCode: string; mobileNo: string;
}
interface WithdrawRow { name: string; registrationIdNo: string; contactCd: string; profileId: string; ceasedDate: string; ceasedReason: string; ceasedReasonOther: string }

const appointRows = reactive<AppointRow[]>([]);
const withdrawRows = reactive<WithdrawRow[]>([]);
const dateMenus = reactive<Record<string, boolean>>({}); // 달력 메뉴 열림상태(키별)

const emptyAddr = (): Addr => ({ type: 'F', blkhseNo: '', streetName: '', buildingName: '', levelNo: '', unitNo: '', postalCode: '', foreignAddr1: '', foreignAddr2: '' });

function seedFromDocument() {
  const source = fd.value || {};
  // CEO/MD 패널은 companyName / companyRegistrationNo(+ presetForm.values.company_*) 로 저장한다.
  const pv = (source.presetForm && typeof source.presetForm === 'object' && source.presetForm.values) || {};
  entityName.value = st(pick(source.company_name, source.companyName, pv.company_name, props.taskData?.entityName, source.entityName));
  entityUEN.value = st(pick(source.company_registration_no, source.companyRegistrationNo, pv.company_registration_no, source.entityUEN, props.taskData?.entityUEN)).replace(/\s/g, '');
  entityType.value = st(pick(source.entityType, 'LC')).toUpperCase();
  filingAgentUEN.value = st(pick(source.filingAgentUEN, sessionStore.getCustomerInfo()?.regNo)).replace(/\s/g, '').toUpperCase();

  // 재열람 — 이전 제출 스냅샷(FORM_DATA)이 있으면 편집값 그대로 복원하고 종료.
  if (Array.isArray(source.awoAppointList) || Array.isArray(source.awoWithdrawList)) {
    appointRows.splice(0, appointRows.length, ...(Array.isArray(source.awoAppointList) ? source.awoAppointList : []));
    withdrawRows.splice(0, withdrawRows.length, ...(Array.isArray(source.awoWithdrawList) ? source.awoWithdrawList : []));
    void normalizeNationalities();
    return;
  }

  const preset = (source.presetForm && typeof source.presetForm === 'object') ? source.presetForm : source;
  const values: Record<string, any> = (preset.values && typeof preset.values === 'object') ? preset.values : source;
  const local = (preset.local && typeof preset.local === 'object') ? preset.local : {};

  if (positionTask.value) { seedPositionTask(positionTask.value.key, values, preset, source); return; }
  const idsFrom = (scope: string, listed: any): number[] => {
    let ids: number[] = Array.isArray(listed) ? listed.map(Number) : [];
    if (ids.length === 0) {
      const s = new Set<number>();
      for (const k of Object.keys(values)) { const m = k.match(new RegExp(`^${scope}_(\\d+)/`)); if (m) s.add(Number(m[1])); }
      ids = [...s].sort((a, b) => a - b);
    }
    return ids;
  };
  const g = (scope: string, id: number, key: string) => st(values[`${scope}_${id}/${key}`]);

  /** 자유서식 주소 → Addr. SG 로 감지되면 local 필드 파싱, 아니면 foreign 라인 분할. */
  const buildAddr = (raw: string): Addr => {
    const a = emptyAddr();
    const t = st(raw);
    if (!t) return a;
    if (isSgAddressText(t)) {
      const p = parseSgAddress(t);
      Object.assign(a, p);
    } else {
      a.type = 'F';
      [a.foreignAddr1, a.foreignAddr2] = splitAddr(t);
    }
    return a;
  };
  const a: AppointRow[] = idsFrom('f45', local.newIds)
    .filter((id) => g('f45', id, 'director_name') || g('f45', id, 'director_identification_no'))
    .map((id) => {
      const idNo = g('f45', id, 'director_identification_no');
      const resi = buildAddr(g('f45', id, 'director_address'));
      const contact = buildAddr(g('f45', id, 'director_alternate_address'));
      // contact 가 비어있거나 residential 과 같은 원문이면 "same as residential" 로 간주.
      const sameKey = (x: Addr) => `${x.type}|${x.blkhseNo}|${x.streetName}|${x.postalCode}|${x.foreignAddr1}`;
      const contactSame = sameKey(contact) === '' || sameKey(contact) === 'F||||' || sameKey(contact) === sameKey(resi);
      return {
        name: g('f45', id, 'director_name'), registrationIdNo: idNo, contactCd: g('f45', id, 'director_contact_cd'),
        idType: mapIdType(g('f45', id, 'director_id_type'), idNo),
        // 국적명 → B04 TOTAL_CODE (예: "Korean, South" / "KR" 어느 쪽으로 와도 KR 로 정규화).
        nationality: g('f45', id, 'director_nationality'),  // 표시용 raw. 코드 변환은 buildAppointList 에서.
        entryDate: g('f45', id, 'appointment_effective_date'),
        // 문서에 있으면 사용, 없으면 enrichFromContacts 가 컨택트에서 폴백.
        // Form 45 (공통 최신본) 은 director_date_of_birth — 예전 키 director_birth_date 도 허용.
        birthDate: (() => { const b = g('f45', id, 'director_date_of_birth') || g('f45', id, 'director_birth_date'); return normalizeDateInputToIso(b) || b; })(),
        profileId: g('f45', id, 'director_profile_id'),
        resi, contactSame, contact,
        email: g('f45', id, 'director_email'), countryCd: g('f45', id, 'director_country_cd'),
        noAreaCode: false, mobileNoAreaCode: g('f45', id, 'director_area_code'), mobileNo: g('f45', id, 'director_mobile_no'),
      };
    });
  const w: WithdrawRow[] = idsFrom('res', local.resignIds)
    .filter((id) => g('res', id, 'director_name') || g('res', id, 'director_identification_no'))
    .map((id) => ({
      name: g('res', id, 'director_name'), registrationIdNo: g('res', id, 'director_identification_no'),
      contactCd: g('res', id, 'director_contact_cd'), profileId: '',
      ceasedDate: g('res', id, 'resignation_date'), ceasedReason: 'Resignation', ceasedReasonOther: '',
    }));

  // fallback — 예전 1인 Appoint Director(B32101) 폼 태스크: 평면 필드(directorName …)
  if (a.length === 0 && st(source.directorName)) {
    const idNo = st(source.directorIdentificationNo);
    const resi = buildAddr(st(source.directorAddress));
    const contact = buildAddr(st(source.directorAlternateAddress));
    a.push({
      name: st(source.directorName), registrationIdNo: idNo, contactCd: st(pick(source.contactCd, source.officerNameCd)),
      idType: mapIdType(st(source.directorIdTypeCd), idNo), nationality: st(source.directorNationality),
      entryDate: st(pick(source.appointmentEffectiveDate, source.appointmentDate, source.appointedDate)),
      birthDate: normalizeDateInputToIso(st(source.directorDateOfBirth)) || st(source.directorDateOfBirth), profileId: '',
      resi, contactSame: !st(source.directorAlternateAddress), contact,
      email: st(source.directorEmail), countryCd: st(source.directorMobileCountryCd), noAreaCode: false,
      mobileNoAreaCode: st(source.directorMobileAreaCode), mobileNo: st(source.directorContactNo),
    });
  }
  // fallback — DR doc (name+id only)
  if (a.length === 0 && Array.isArray(source.new_directors)) {
    source.new_directors.filter((d: any) => st(d?.director_name) || st(d?.director_identification_no)).forEach((d: any) =>
      a.push({ name: st(d?.director_name), registrationIdNo: st(d?.director_identification_no), idType: mapIdType('', st(d?.director_identification_no)), nationality: '', entryDate: '', contactCd: '', birthDate: '', profileId: '', resi: emptyAddr(), contactSame: true, contact: emptyAddr(), email: '', countryCd: '', noAreaCode: false, mobileNoAreaCode: '', mobileNo: '' }));
  }
  if (w.length === 0 && Array.isArray(source.resign_directors)) {
    source.resign_directors.filter((d: any) => st(d?.director_name) || st(d?.director_identification_no)).forEach((d: any) =>
      w.push({ name: st(d?.director_name), registrationIdNo: st(d?.director_identification_no), contactCd: '', profileId: '', ceasedDate: '', ceasedReason: 'Resignation', ceasedReasonOther: '' }));
  }

  appointRows.splice(0, appointRows.length, ...a);
  withdrawRows.splice(0, withdrawRows.length, ...w);
  void enrichFromContacts();
}
/** 자유서식 주소 → Addr (SG 면 local 파싱, 아니면 foreign 라인 분할). */
function buildAddrFromText(raw: string): Addr {
  const a = emptyAddr();
  const t = st(raw);
  if (!t) return a;
  if (isSgAddressText(t)) Object.assign(a, parseSgAddress(t));
  else [a.foreignAddr1, a.foreignAddr2] = splitAddr(t);
  return a;
}
/**
 * CEO(B32114) / MD(B32115) 시드 — 문서 값(<k>_name·<k>_appointment_date / resigning_<k>_name·<k>_resignation_date)
 *   + 패널이 저장한 presetForm.persons(인물 선택 시 ID·국적·주소·연락처). 고른 결의 쪽만 행을 만든다.
 */
function seedPositionTask(k: string, values: Record<string, any>, preset: any, source: any) {
  const persons = (preset.persons && typeof preset.persons === 'object') ? preset.persons : {};
  /** 이름이 같을 때만 저장된 인물 정보를 쓴다(이름을 직접 고쳤으면 컨택트 검색으로 보완). */
  const personFor = (side: 'appoint' | 'resign', name: string): Record<string, any> => {
    const p = persons[side] || {};
    return st(p.name).toUpperCase() === st(name).toUpperCase() ? p : {};
  };
  const K = k.charAt(0).toUpperCase() + k.slice(1);
  const a: AppointRow[] = [];
  const w: WithdrawRow[] = [];
  const action = positionAction.value;
  const apptName = st(pick(values[`${k}_name`], source[`${k}Name`]));
  if (action !== 'withdraw' && apptName) {
    const p = personFor('appoint', apptName);
    const idNo = st(p.idNo);
    a.push({
      name: apptName, registrationIdNo: idNo, contactCd: st(p.contactCd),
      idType: mapIdType(st(p.idType), idNo), nationality: st(p.nationality),
      entryDate: st(pick(values[`${k}_appointment_date`], source[`${k}AppointmentDate`])),
      birthDate: normalizeDateInputToIso(st(p.dateOfBirth)) || st(p.dateOfBirth), profileId: '',
      resi: buildAddrFromText(st(p.address)), contactSame: !st(p.alternateAddress), contact: buildAddrFromText(st(p.alternateAddress)),
      email: st(p.email), countryCd: st(p.countryCd), noAreaCode: false, mobileNoAreaCode: st(p.areaCode), mobileNo: st(p.mobileNo),
    });
  }
  const resName = st(pick(values[`resigning_${k}_name`], source[`resigning${K}Name`]));
  if (action !== 'appoint' && resName) {
    const p = personFor('resign', resName);
    w.push({
      name: resName, registrationIdNo: st(p.idNo), contactCd: st(p.contactCd), profileId: '',
      ceasedDate: st(pick(values[`${k}_resignation_date`], source[`${k}ResignationDate`])), ceasedReason: 'Resignation', ceasedReasonOther: '',
    });
  }
  appointRows.splice(0, appointRows.length, ...a);
  withdrawRows.splice(0, withdrawRows.length, ...w);
  void enrichFromContacts();
}
watch(fd, seedFromDocument, { immediate: true });

const gvc = (c: any, ...ks: string[]) => { for (const k of ks) { const v = c?.[k]; if (v != null && String(v).trim() !== '') return String(v).trim(); } return ''; };
const addrEmpty = (a: Addr) => !a.foreignAddr1 && !a.streetName && !a.postalCode;
/** 컨택트 상세의 칸별 주소 → Addr. 상세는 한 줄 주소가 아니라 POSTAL_CODE·BLOCK_NO·STREET_NAME… /
 *  FOREIGN_ADDRESS_LINE_1·2 로 준다. prefix 'ALT_' = Contact(Alternate) Address. */
function addrFromContactFields(c: any, prefix = ''): Addr {
  const a = emptyAddr();
  const f = (k: string) => gvc(c, prefix + k);
  const postal = f('POSTAL_CODE');
  const street = f('STREET_NAME');
  const f1 = f('FOREIGN_ADDRESS_LINE_1');
  if (f1 && !street) {
    Object.assign(a, { type: 'F', foreignAddr1: f1, foreignAddr2: f('FOREIGN_ADDRESS_LINE_2') });
  } else if (postal || street) {
    Object.assign(a, { type: 'S', blkhseNo: f('BLOCK_NO'), streetName: street, buildingName: f('BUILDING_NAME'), levelNo: f('ADDR_LEVEL'), unitNo: f('ADDR_UNIT'), postalCode: postal });
  }
  return a;
}

/** 글로벌 컨택트 1건 조회 — contactCd 우선, 없으면 ID번호/이름으로 검색 후 상세(getContactById)까지. */
async function fetchContact(contactCd: string, idNo: string, name: string): Promise<any> {
  try {
    if (contactCd) { const r: any = await contactsApi.getContactById(contactCd); return r?.contact || r?.data?.contact || r?.data || null; }
    const idU = st(idNo).toUpperCase(); const nmU = st(name).toUpperCase();
    for (const key of [idNo, name].filter(Boolean)) {
      const r: any = await contactsApi.getContacts({ search: key, limit: 10 } as any);
      const list = r?.contacts || r?.data?.contacts || r?.data || [];
      const arr: any[] = Array.isArray(list) ? list : [];
      const hit = arr.find((x) => st(gvc(x, 'CONTACT_REG_NO', 'contactRegNo', 'identificationNo')).toUpperCase() === idU && !!idU)
        || arr.find((x) => st(gvc(x, 'CONTACT_NAME', 'contactName', 'name')).toUpperCase() === nmU && !!nmU)
        || arr[0] || null;
      const cc = hit && (hit.contactCd || hit.CONTACT_CD);
      if (cc) { const full: any = await contactsApi.getContactById(cc); return full?.contact || full?.data?.contact || full?.data || hit; }
      if (hit) return hit;
    }
  } catch (e) { console.warn('[AWO] fetchContact failed', e); }
  return null;
}

/** 선임: 빈 연락처/주소 채움. 해임: bizfile profileId(글로벌 컨택트 PROFILE_ID)를 동적으로 채움. */
async function enrichFromContacts() {
  await countryReady;
  await nationalityReady;
  await b04Bigo1Ready;
  // 선임 — 이메일/연락처/국가/idType/생년월일/profileId 를 컨택트에서 채운다.
  //   birthDate, profileId 는 폼에 입력 UI 가 없어도 payload 에 실려나가야 하므로 여기서 세팅.
  for (const row of appointRows) {
    const resiEmpty = !row.resi.foreignAddr1 && !row.resi.streetName && !row.resi.postalCode;
    if (row.email && row.mobileNo && row.countryCd && row.birthDate && row.profileId && row.registrationIdNo && row.nationality && !resiEmpty) continue;
    const c = await fetchContact(row.contactCd, row.registrationIdNo, row.name);
    if (!c) continue;
    // CEO/MD 는 문서에 ID·주소 칸이 없다 — 비었으면 컨택트에서.
    if (!row.registrationIdNo) row.registrationIdNo = gvc(c, 'contactRegNo', 'CONTACT_REG_NO', 'identificationNo');
    if (resiEmpty) {
      const addr = gvc(c, 'residentialAddress', 'primaryAddress', 'address', 'ADDRESS');
      if (addr) row.resi = buildAddrFromText(addr);
      else {
        const r = addrFromContactFields(c);
        if (!addrEmpty(r)) row.resi = r;
      }
      // 컨택트에 Contact(Alternate) Address 가 따로 있고 거주지와 다르면 그 값으로.
      const alt = addrFromContactFields(c, 'ALT_');
      const key = (x: Addr) => `${x.type}|${x.blkhseNo}|${x.streetName}|${x.postalCode}|${x.foreignAddr1}`;
      if (row.contactSame && !addrEmpty(alt) && key(alt) !== key(row.resi)) { row.contact = alt; row.contactSame = false; }
    }
    if (!row.email) row.email = gvc(c, 'contactEmail', 'CONTACT_EMAIL', 'email') || (Array.isArray(c.emails) ? st(c.emails[0]) : '');
    if (!row.countryCd) row.countryCd = resolveCountry(gvc(c, 'countryCd', 'COUNTRY_CD'));
    if (!row.mobileNoAreaCode) row.mobileNoAreaCode = gvc(c, 'areaCode', 'AREA_CODE');
    if (!row.mobileNo) row.mobileNo = gvc(c, 'mobileNo', 'MOBILE_NO') || (Array.isArray(c.mobileNos) ? st(c.mobileNos[0]) : '');
    if (!row.idType) row.idType = mapIdType(gvc(c, 'idTypeCd', 'ID_TYPE_CD'), row.registrationIdNo);
    // 컨택트 상세는 국적을 NATIONALITY_CD(B04 코드)로만 준다 — 예전엔 이 키를 안 읽어 국적이 비었다(운영 Task 2214).
    if (!row.nationality) row.nationality = gvc(c, 'nationality', 'nationalityName', 'NATIONALITY_NAME', 'NATIONALITY_CD', 'nationalityCd');
    if (!row.birthDate) {
      const bd = gvc(c, 'birthDate', 'BIRTH_DATE', 'dateOfBirth', 'DATE_OF_BIRTH', 'dob', 'DOB');
      // ISO(YYYY-MM-DD) 로 표준화 — 컨택트가 어떤 포맷을 담고 있든 dateUtils 로 정규화.
      row.birthDate = bd ? (normalizeDateInputToIso(bd) || bd.slice(0, 10)) : '';
    }
    if (!row.profileId) row.profileId = gvc(c, 'profileId', 'PROFILE_ID');
  }
  // 해임 — bizfile 임원 식별용 PROFILE_ID (고정값 아님, 컨택트에서 동적 조회)
  for (const row of withdrawRows) {
    if (row.profileId) continue;
    const c = await fetchContact(row.contactCd, row.registrationIdNo, row.name);
    if (!c) continue;
    row.profileId = gvc(c, 'profileId', 'PROFILE_ID');
    if (!row.registrationIdNo) row.registrationIdNo = gvc(c, 'contactRegNo', 'CONTACT_REG_NO', 'identificationNo');
    console.debug('[AWO] withdraw profileId resolved for', row.name, '→', row.profileId);
  }
  await normalizeNationalities();
}

function removeAppoint(i: number) { appointRows.splice(i, 1); }
function removeWithdraw(i: number) { withdrawRows.splice(i, 1); }

const canRun = computed(() => !!entityUEN.value && (appointRows.length > 0 || withdrawRows.length > 0));

function addrToBizfile(prefix: '' | 'contact', a: Addr) {
  const P = (k: string) => (prefix ? prefix + k.charAt(0).toUpperCase() + k.slice(1) : k);
  const local = a.type === 'S';
  return {
    [prefix ? 'contactAddressType' : 'addressType']: a.type,
    [P('blkhseNo')]: local ? a.blkhseNo : '',
    [P('streetName')]: local ? a.streetName : '',
    [P('buildingName')]: local ? a.buildingName : '',
    [P('levelNo')]: local ? a.levelNo : '',
    [P('unitNo')]: local ? a.unitNo : '',
    [P('postalCode')]: local ? a.postalCode : '',
    [P('foreignAddr1')]: local ? '' : a.foreignAddr1,
    [P('foreignAddr2')]: local ? '' : a.foreignAddr2,
  };
}
/** SG 주소가 foreign 필드에 있으면 local 로 스왑 (프론트 백스톱). */
function coerceSgAddress(a: Addr): Addr {
  const raw = st(a.foreignAddr1) + ' ' + st(a.foreignAddr2);
  if (!isSgAddressText(raw)) return a;
  const p = parseSgAddress(raw.trim());
  return { ...a, ...p, type: 'S', foreignAddr1: '', foreignAddr2: '' } as Addr;
}
function buildAppointList() {
  return appointRows.filter((r) => r.name || r.registrationIdNo).map((r) => {
    // resi/contact 각각 SG 주소면 local 로 자동 스왑.
    const resi = coerceSgAddress(r.resi);
    const contactRaw = r.contactSame ? resi : r.contact;
    const contact = coerceSgAddress(contactRaw);
    return {
      entityType: entityType.value,
      addPositionHolder: {
        // nationality: B04 TOTAL_CODE (ISO2, "KR"). 라벨/원문이 들어오면 여기서 정규화.
        name: r.name, registrationIdNo: r.registrationIdNo, idType: r.idType, nationality: toNationalityCode(r.nationality),
        // profileId: 컨택트/문서에서 확보되면 실어보냄. 없으면 신규 등록 (bizfile 이 생성).
        profileId: r.profileId || '',
        // birthDate: 컨택트/문서에서 확보. 데몬이 이 값으로 hasBirthDate 자동 세팅.
        birthDate: r.birthDate || '',
        addPositionHeld: [{ positionHeld: positionHeld.value, entryDate: r.entryDate }],
        ...addrToBizfile('', resi),
        // 데몬이 profileId 유무로 resiAddressSource("A"/"O") 자동 세팅하지만, 보수적으로 여기서도 힌트.
        resiAddressSource: r.profileId ? 'A' : (resi.type === 'S' ? 'O' : ''),
        ...addrToBizfile('contact', contact),
        email: r.email, mobileNoCountryCode: countryBigo3(r.countryCd), mobileNoAreaCode: r.noAreaCode ? '' : r.mobileNoAreaCode, mobileNo: r.mobileNo,
      },
    };
  });
}
function buildWithdrawList() {
  return withdrawRows.filter((r) => r.name || r.registrationIdNo).map((r) => ({
    entityType: entityType.value,
    withdrawPositionHolder: {
      name: r.name, profileId: r.profileId, registrationIdNo: r.registrationIdNo,
      withdrawPositionHeld: [{ positionHeld: positionHeld.value, ceasedDate: r.ceasedDate, ceasedReason: r.ceasedReason === 'OTH' ? (r.ceasedReasonOther || 'Others') : r.ceasedReason }],
    },
  }));
}

const errorMsg = ref('');
const busy = ref(false);
const draftId = ref('');
const showLoginDialog = ref(false);
const activeSessionId = ref('');
const activeMstIdx = ref<number | null>(null); // 로그인 세션 로그 마스터 — save-draft 스텝 로그에 사용

function onRunClick() {
  if (!canRun.value) { showWarning('E-Filing', 'Company UEN or appoint/withdraw list is empty.'); return; }
  if (!filingAgentUEN.value) { showWarning('E-Filing', 'Filing Agent UEN not found. Please check the session.'); return; }
  errorMsg.value = ''; activeSessionId.value = ''; showLoginDialog.value = true;
}
function onAuthenticated(payload: { sessionId: string; mstIdx?: number | null }) { showLoginDialog.value = false; activeSessionId.value = payload.sessionId; activeMstIdx.value = payload.mstIdx ?? null; void executeSaveDraft(); }

async function executeSaveDraft() {
  if (busy.value) return;
  errorMsg.value = ''; busy.value = true;
  try {
    const appointList = buildAppointList();
    const withdrawList = buildWithdrawList();
    const requestPayload = { entityUEN: entityUEN.value, entityName: entityName.value, entityType: entityType.value, filingAgentUEN: filingAgentUEN.value, appointList, withdrawList };
    const res: any = await apiClient.post('/tilkoapi/bizfile/officer/save-draft', {
      sessionId: activeSessionId.value, mstIdx: activeMstIdx.value, ...requestPayload, supportingDocuments: [], forceSkipRegenerate: false,
    });
    const data = res?.data?.data || res?.data || {};
    draftId.value = String(data.draft_id || '');

    // ── Start Task 데이터 업데이트: L_TASK_EFILING_DATA 저장 + L_START_TASK 완료(B36003) ──
    if (props.taskId) {
      const nowIso = new Date().toISOString();
      // 재열람 시 편집값 복원용으로 리스트 스냅샷을 FORM_DATA 로 보관.
      const formSnapshot = {
        awoAppointList: JSON.parse(JSON.stringify(appointRows)),
        awoWithdrawList: JSON.parse(JSON.stringify(withdrawRows)),
        awoDraftId: draftId.value,
        company_name: entityName.value, company_registration_no: entityUEN.value,
      };
      try {
        await saveEFilingData(props.taskId, {
          taskTypeCd: taskTypeCd.value, entityCd: props.entityCd || null,
          formData: formSnapshot, requestPayload, responseData: { draftId: draftId.value, result: data.result || null },
          status: 'B36003', isSuccess: true, submittedAt: nowIso, completedAt: nowIso, isDraft: false,
        });
      } catch (e2) { console.warn('[AWO] saveEFilingData failed', e2); }
      try { await updateEFilingComplete(props.taskId); } catch (e3) { console.warn('[AWO] updateEFilingComplete failed', e3); }
    }

    // 완료 알림/상태전환은 부모(handleEFilingComplete)가 처리 — raw draftId 알림은 띄우지 않음.
    emit('submit', { draftId: draftId.value, result: data.result || null });
  } catch (e: any) {
    const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || 'E-Filing failed';
    errorMsg.value = msg; emit('error', e);
    await showAlert({ title: 'E-Filing failed', message: msg, type: 'error' });
  } finally { busy.value = false; activeSessionId.value = ''; }
}
function handleBack() { emit('back'); }
</script>

<template>
  <div class="awo">
    <div class="awo-head">
      <v-icon color="primary">mdi-account-switch</v-icon>
      <h2>{{ pageTitle }} — E-Filing</h2>
    </div>

    <!-- Company (name + UEN only) -->
    <div class="grid2 mb-2">
      <v-text-field v-model="entityName" label="Company Name" density="compact" variant="outlined" hide-details :readonly="readOnly" />
      <v-text-field v-model="entityUEN" label="Entity UEN" density="compact" variant="outlined" hide-details :readonly="readOnly" />
    </div>

    <!-- Appoint (CEO/MD 는 고른 결의에 따라) -->
    <template v-if="showAppoint">
    <div class="sec-head">
      <h3><v-icon size="small">mdi-account-plus-outline</v-icon> Appoint · {{ appointRows.length }}</h3>
    </div>
    <div v-if="appointRows.length === 0" class="empty">No appointments — check that {{ emptyWho }} added in the document.</div>
    <div v-for="(row, i) in appointRows" :key="'a' + i" class="holder">
      <div class="holder-top"><span>Appoint #{{ i + 1 }}</span><v-btn v-if="!readOnly" icon="mdi-close" size="x-small" variant="text" @click="removeAppoint(i)" /></div>
      <div class="grid2">
        <v-text-field v-model="row.name" label="Name" density="compact" variant="outlined" hide-details :readonly="readOnly" />
        <v-text-field v-model="row.registrationIdNo" label="ID No." density="compact" variant="outlined" hide-details :readonly="readOnly" />
        <v-select v-model="row.idType" :items="ID_TYPE_OPTIONS" label="ID Type" density="compact" variant="outlined" hide-details :readonly="readOnly" />
        <v-autocomplete v-model="row.nationality" :items="nationalityOptions" item-title="title" item-value="value" label="Nationality" density="compact" variant="outlined" hide-details :readonly="readOnly" />
        <v-menu v-model="dateMenus['a' + i]" :close-on-content-click="false" location="bottom start">
          <template #activator="{ props: mp }">
            <v-text-field v-bind="mp" :model-value="fmtDate(row.entryDate)" label="Appointment Date" density="compact" variant="outlined" hide-details readonly append-inner-icon="mdi-calendar" :disabled="readOnly" />
          </template>
          <v-date-picker :model-value="isoToDate(row.entryDate)" hide-header show-adjacent-months @update:model-value="(d: any) => { row.entryDate = pickedToIso(d); dateMenus['a' + i] = false; }" />
        </v-menu>
      </div>

      <!-- Residential address -->
      <div class="addr-block">
        <div class="addr-title">Residential Address</div>
        <v-radio-group v-model="row.resi.type" inline density="compact" hide-details :disabled="readOnly" class="addr-radio">
          <v-radio v-for="o in ADDRESS_TYPES" :key="o.value" :label="o.title" :value="o.value" />
        </v-radio-group>
        <template v-if="row.resi.type === 'F'">
          <v-text-field v-model="row.resi.foreignAddr1" label="Foreign Address Line 1" density="compact" variant="outlined" hide-details :readonly="readOnly" class="mb-2" />
          <v-text-field v-model="row.resi.foreignAddr2" label="Foreign Address Line 2" density="compact" variant="outlined" hide-details :readonly="readOnly" />
        </template>
        <div v-else class="grid3">
          <v-text-field v-model="row.resi.blkhseNo" label="Block/House No." density="compact" variant="outlined" hide-details :readonly="readOnly" />
          <v-text-field v-model="row.resi.streetName" label="Street Name" density="compact" variant="outlined" hide-details :readonly="readOnly" />
          <v-text-field v-model="row.resi.buildingName" label="Building Name" density="compact" variant="outlined" hide-details :readonly="readOnly" />
          <v-text-field v-model="row.resi.levelNo" label="Level" density="compact" variant="outlined" hide-details :readonly="readOnly" />
          <v-text-field v-model="row.resi.unitNo" label="Unit" density="compact" variant="outlined" hide-details :readonly="readOnly" />
          <v-text-field v-model="row.resi.postalCode" label="Postal Code" density="compact" variant="outlined" hide-details :readonly="readOnly" />
        </div>
      </div>

      <!-- Contact address -->
      <div class="addr-block">
        <div class="addr-title">
          Contact Address
          <v-checkbox v-model="row.contactSame" label="Same as residential" density="compact" hide-details :disabled="readOnly" class="same-chk" />
        </div>
        <template v-if="!row.contactSame">
          <v-radio-group v-model="row.contact.type" inline density="compact" hide-details :disabled="readOnly">
            <v-radio v-for="o in ADDRESS_TYPES" :key="o.value" :label="o.title" :value="o.value" />
          </v-radio-group>
          <template v-if="row.contact.type === 'F'">
            <v-text-field v-model="row.contact.foreignAddr1" label="Foreign Address Line 1" density="compact" variant="outlined" hide-details :readonly="readOnly" class="mb-2" />
            <v-text-field v-model="row.contact.foreignAddr2" label="Foreign Address Line 2" density="compact" variant="outlined" hide-details :readonly="readOnly" />
          </template>
          <div v-else class="grid3">
            <v-text-field v-model="row.contact.blkhseNo" label="Block/House No." density="compact" variant="outlined" hide-details :readonly="readOnly" />
            <v-text-field v-model="row.contact.streetName" label="Street Name" density="compact" variant="outlined" hide-details :readonly="readOnly" />
            <v-text-field v-model="row.contact.buildingName" label="Building Name" density="compact" variant="outlined" hide-details :readonly="readOnly" />
            <v-text-field v-model="row.contact.levelNo" label="Level" density="compact" variant="outlined" hide-details :readonly="readOnly" />
            <v-text-field v-model="row.contact.unitNo" label="Unit" density="compact" variant="outlined" hide-details :readonly="readOnly" />
            <v-text-field v-model="row.contact.postalCode" label="Postal Code" density="compact" variant="outlined" hide-details :readonly="readOnly" />
          </div>
        </template>
      </div>

      <!-- Email (own row) -->
      <v-text-field v-model="row.email" label="Email" density="compact" variant="outlined" hide-details :readonly="readOnly" class="mt-2" />
      <!-- Mobile: Country Code (common code) / Area Code (+ no-area checkbox) / Mobile No. -->
      <div class="phone-grid mt-2">
        <v-autocomplete v-model="row.countryCd" :items="countryOptions" item-title="title" item-value="value" label="Country Code" density="compact" variant="outlined" hide-details :readonly="readOnly" />
        <div class="area-cell">
          <v-text-field v-model="row.mobileNoAreaCode" label="Area Code" density="compact" variant="outlined" hide-details :readonly="readOnly || row.noAreaCode" :disabled="row.noAreaCode" />
          <v-checkbox v-model="row.noAreaCode" label="No area code" density="compact" hide-details :disabled="readOnly" class="area-chk" />
        </div>
        <v-text-field v-model="row.mobileNo" label="Mobile No." density="compact" variant="outlined" hide-details :readonly="readOnly" />
      </div>
    </div>

    </template>

    <!-- Withdraw (B32101 Appoint Director 는 선임만 — 숨김 · CEO/MD 는 고른 결의에 따라) -->
    <template v-if="showWithdraw">
    <div class="sec-head mt-4">
      <h3><v-icon size="small">mdi-account-minus-outline</v-icon> Withdraw · {{ withdrawRows.length }}</h3>
    </div>
    <div v-if="withdrawRows.length === 0" class="empty">No withdrawals — check that {{ emptyWho }} added in the document.</div>
    <div v-for="(row, i) in withdrawRows" :key="'w' + i" class="holder">
      <div class="holder-top"><span>Withdraw #{{ i + 1 }}</span><v-btn v-if="!readOnly" icon="mdi-close" size="x-small" variant="text" @click="removeWithdraw(i)" /></div>
      <div class="grid2">
        <v-text-field v-model="row.name" label="Name" density="compact" variant="outlined" hide-details :readonly="readOnly" />
        <v-text-field v-model="row.registrationIdNo" label="ID No." density="compact" variant="outlined" hide-details :readonly="readOnly" />
        <v-menu v-model="dateMenus['w' + i]" :close-on-content-click="false" location="bottom start">
          <template #activator="{ props: mp }">
            <v-text-field v-bind="mp" :model-value="fmtDate(row.ceasedDate)" label="Cessation Date" density="compact" variant="outlined" hide-details readonly append-inner-icon="mdi-calendar" :disabled="readOnly" />
          </template>
          <v-date-picker :model-value="isoToDate(row.ceasedDate)" hide-header show-adjacent-months @update:model-value="(d: any) => { row.ceasedDate = pickedToIso(d); dateMenus['w' + i] = false; }" />
        </v-menu>
        <v-select v-model="row.ceasedReason" :items="CESSATION_REASONS" label="Cessation Reason" density="compact" variant="outlined" hide-details :readonly="readOnly" />
        <v-text-field v-if="row.ceasedReason === 'OTH'" v-model="row.ceasedReasonOther" label="Please specify" density="compact" variant="outlined" hide-details :readonly="readOnly" class="span2" />
      </div>
    </div>
    </template>

    <v-alert v-if="errorMsg" type="error" density="compact" variant="tonal" class="mt-4">{{ errorMsg }}</v-alert>
    <!-- draftId 는 내부에서만 보관하고 화면에는 노출하지 않는다 (완료 안내는 부모 handleEFilingComplete 가 처리). -->
    <v-alert v-if="draftId" type="success" density="compact" variant="tonal" class="mt-4">
      E-Filing draft saved successfully.
    </v-alert>

    <div class="actions">
      <v-btn variant="outlined" color="primary" :disabled="busy" @click="handleBack">Back</v-btn>
      <v-btn color="primary" variant="flat" prepend-icon="mdi-file-document-outline" :disabled="!canRun || busy" :loading="busy" @click="onRunClick">Submit</v-btn>
    </div>

    <SgPyLoginDialog v-model="showLoginDialog" :filing-agent-u-e-n="filingAgentUEN" @authenticated="onAuthenticated" />
  </div>
</template>

<style scoped>
/* Preset Studio 캔버스(회색) 위에 올라가는 하얀 문서 판넬 */
.awo {
  width: 880px; max-width: 100%; margin: 0 auto;
  background: #fff; border: 1px solid #E5E8EB; border-radius: 16px;
  box-shadow: 0 1px 3px rgba(20, 30, 60, 0.06);
  padding: 28px 32px;
}
.awo-head { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; }
.awo-head h2 { font-size: 18px; font-weight: 700; }
.grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
.phone-grid { display: grid; grid-template-columns: 1.2fr 1fr 1fr; gap: 10px; align-items: start; }
.area-cell { display: flex; flex-direction: column; }
.area-chk :deep(.v-label) { font-size: 11.5px; }
.area-chk { margin-top: -4px; }
.sec-head { display: flex; align-items: center; justify-content: space-between; margin: 14px 0 8px; }
.sec-head h3 { font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 6px; }
.empty { font-size: 12.5px; color: #6b7280; padding: 12px; border: 1px dashed #d5dae4; border-radius: 8px; }
.holder { border: 1px solid #e3e7ee; border-radius: 10px; padding: 14px; margin-bottom: 12px; }
.holder-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; font-size: 12.5px; font-weight: 700; color: #374151; }
.addr-block { margin-top: 14px; }
.addr-title { font-size: 12.5px; font-weight: 600; color: #4b5563; margin-bottom: 4px; display: flex; align-items: center; gap: 12px; }
.addr-radio { margin: 2px 0 12px !important; }
.same-chk :deep(.v-label) { font-size: 12px; }
.span2 { grid-column: 1 / -1; }
.actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }
@media (max-width: 760px) { .grid2, .grid3, .phone-grid { grid-template-columns: 1fr; } }
</style>
